'use client';

import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import type { BoAOfferConfig } from './types';
import { getBestOfAwardsReviewConfig, getBestOfAwardsRoundupConfig } from './utils';

export interface BestOfAwardsConfigProviderProps extends PropsWithChildren {
  isExperimentActive?: boolean;
  type: 'roundup' | 'review';
  slug?: string;
}

type BestOfAwardsConfigContext = Required<
  Pick<BestOfAwardsConfigProviderProps, 'isExperimentActive' | 'type' | 'slug'>
>;

type BestOfAwardsConfigHookReturn =
  | { isExperimentActive: false; hasBestOfAward: false; config: null }
  | { isExperimentActive: true; hasBestOfAward: true; config: BoAOfferConfig }
  | { isExperimentActive: true; hasBestOfAward: false; config: null };

const BoAConfigContext = createContext<BestOfAwardsConfigContext>({
  isExperimentActive: false,
  type: 'roundup',
  slug: '',
});

export function BestOfAwardsConfigProvider({
  children,
  isExperimentActive = false,
  slug = '',
  type,
}: BestOfAwardsConfigProviderProps) {
  return (
    <BoAConfigContext.Provider value={{ isExperimentActive, slug, type }}>
      {children}
    </BoAConfigContext.Provider>
  );
}

export function useBestOfAwardsConfig(marketplaceId: string): BestOfAwardsConfigHookReturn {
  const { isExperimentActive, slug, type } = useContext(BoAConfigContext);

  if (!isExperimentActive) {
    return { config: null, hasBestOfAward: false, isExperimentActive: false };
  }

  const config =
    type === 'roundup'
      ? getBestOfAwardsRoundupConfig(slug, marketplaceId)
      : getBestOfAwardsReviewConfig(marketplaceId);

  return config
    ? { config, hasBestOfAward: true, isExperimentActive: true }
    : { config: null, hasBestOfAward: false, isExperimentActive: true };
}
